import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  BottomNavigation,
  BottomNavigationAction,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LibraryMusic as MusicNoteIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarTodayIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as LeaderIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Public as PublicIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Timer as MetronomeIcon,
  GraphicEq as GraphicEqIcon,
  MusicVideo as CapoIcon,
  BarChart as BarChartIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import customFetch from '../../utils/axios';
import { API_ENDPOINTS } from '../../config/api';
import { useTheme } from '../../context/ThemeContext';
import LanguageSelector from '../common/LanguageSelector';
import LanguageDialog from '../common/LanguageDialog';
import { useTranslation } from 'react-i18next';

const getRoleIcon = (role) => {
  switch (role?.toLowerCase()) {
    case 'admin':
    return { icon: <AdminIcon fontSize="small" />, color: '#7B1FA2', tooltip: 'Admin' };
    case 'leader':
    return { icon: <LeaderIcon fontSize="small" />, color: '#1976D2', tooltip: 'Leader' };
    case 'member':
    case 'musician':
    case 'guest':
    return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
    default:
    return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
  }
};

const menuItems = [
  { text: 'sidebar.dashboard', icon: <DashboardIcon />, path: '/app' },
  { text: 'sidebar.services', icon: <CalendarTodayIcon />, path: '/app/services' },
  //{ text: 'sidebar.frequency', icon: <BarChartIcon />, path: '/app/frequency' },
  { text: 'sidebar.songs', icon: <MusicNoteIcon />, path: '/app/songs' },
  { text: 'sidebar.metronome', icon: <MetronomeIcon />, path: '/app/metronome', isPro: true },
  { text: 'sidebar.tuner', icon: <GraphicEqIcon />, path: '/app/tuner', isPro: true },
  { text: 'sidebar.capoHelper', icon: <CapoIcon />, path: '/app/capo-helper', isPro: true },
  { text: 'sidebar.people', icon: <PeopleIcon />, path: '/app/people' },
  { text: 'sidebar.settings', icon: <SettingsIcon />, path: '/app/settings' },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [value, setValue] = useState(location.pathname);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await customFetch.get(`${API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS}?unreadOnly=true`);
        setUnreadCount(response.data?.unreadCount || 0);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };
  
  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    logout();
    navigate('/');
  };
  
  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };
  
  const handleLanguageClick = () => {
    setLanguageDialogOpen(true);
  };
  
  const handleChange = (event, newValue) => {
    setValue(newValue);
    navigate(newValue);
  };
  
  const roleInfo = getRoleIcon(user?.role);
  
  return (
    <>
    <Box
    sx={{
      height: '100%',
      backdropFilter: 'blur(10px)',
      borderRight: '1px solid rgba(255, 255, 255, 0.12)',
      display: 'flex',
      flexDirection: 'column',
    }}
    >
    {/* Logo and Title */}
    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Typography
    variant="h6"
    noWrap
    component="div"
    sx={{
      fontWeight: 700,
      background: 'linear-gradient(135deg, #9945FF 0%, #8A2BE2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }}
    >
    Prelude
    </Typography>
    </Box>
    
    
    {user && (
      <Box sx={{ mb: 2, px: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={roleInfo.tooltip}>
      <IconButton
      size="small"
      sx={{
        backgroundColor: `${roleInfo.color}15`,
        color: roleInfo.color,
        '&:hover': {
          backgroundColor: `${roleInfo.color}25`,
        },
        padding: '4px',
      }}
      >
      {roleInfo.icon}
      </IconButton>
      </Tooltip>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {user.firstName} {user.lastName}
      </Typography>
      </Box>
      {user.church && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {user.church.name}
        </Typography>
      )}
      </Box>
    )}
    
    
    {/* Navigation Items */}
    <List sx={{ flexGrow: 1 }}>
    {menuItems.map((item) => (
      <ListItem key={item.text} disablePadding>
      <ListItemButton
      selected={location.pathname === item.path}
      onClick={() => navigate(item.path)}
      sx={{
        '&.Mui-selected': {
          bgcolor: 'rgba(153, 69, 232, 0.08)',
          '&:hover': {
            bgcolor: 'rgba(153, 69, 232, 0.12)',
          },
        },
        '&:hover': {
          bgcolor: 'rgba(153, 69, 232, 0.04)',
        },
      }}
      >
      <ListItemIcon 
      sx={{ 
        color: location.pathname === item.path ? 'primary.main' : 'inherit'
      }}
      >
      {item.icon}
      </ListItemIcon>
      <ListItemText 
      primary={t(item.text)}
      sx={{
        '& .MuiListItemText-primary': {
          color: location.pathname === item.path ? 'primary.main' : 'inherit',
          fontWeight: location.pathname === item.path ? 600 : 400,
        },
      }}
      />
      {item.isPro && <StarIcon sx={{ color: '#FFD700', fontSize: 16, ml: 0.5 }} />}
      </ListItemButton>
      </ListItem>
    ))}
    {/* Notifications */}
    <ListItem disablePadding>
      <ListItemButton
        selected={location.pathname === '/app/notifications'}
        onClick={() => navigate('/app/notifications')}
        sx={{
          '&.Mui-selected': {
            bgcolor: 'rgba(153, 69, 232, 0.08)',
            '&:hover': { bgcolor: 'rgba(153, 69, 232, 0.12)' },
          },
          '&:hover': { bgcolor: 'rgba(153, 69, 232, 0.04)' },
        }}
      >
        <ListItemIcon sx={{ color: location.pathname === '/app/notifications' ? 'primary.main' : 'inherit' }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary={t('sidebar.notifications', 'Notifications')}
          sx={{
            '& .MuiListItemText-primary': {
              color: location.pathname === '/app/notifications' ? 'primary.main' : 'inherit',
              fontWeight: location.pathname === '/app/notifications' ? 600 : 400,
            },
          }}
        />
      </ListItemButton>
    </ListItem>
    </List>
    
    {/* User Profile and Logout */}
    <Box sx={{ mt: 'auto', p: 2 }}>
    <Divider sx={{ mb: 2 }} />
    
    
    <Divider sx={{ mb: 2 }} />
    
    <Box sx={{ mt: 2 }}>
    <LanguageSelector variant="sidebar" />
    </Box>
    
    <Divider sx={{ mb: 2 }} />
    
    <ListItem disablePadding>
    <ListItemButton
    onClick={handleLogoutClick}
    sx={{
      '&:hover': {
        bgcolor: 'rgba(211, 47, 47, 0.04)',
      },
    }}
    >
    <ListItemIcon sx={{ color: 'error.main' }}>
    <LogoutIcon />
    </ListItemIcon>
    <ListItemText 
    primary="Logout"
    sx={{
      '& .MuiListItemText-primary': {
        color: 'error.main',
        fontWeight: 500,
      },
    }}
    />
    </ListItemButton>
    </ListItem>
    
    </Box>
    </Box>
    
    <Box
    component="nav"
    sx={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      display: { xs: 'block', sm: 'none' },
      zIndex: (theme) => theme.zIndex.appBar,
    }}
    >
    <BottomNavigation
    showLabels
    value={value}
    onChange={handleChange}
    sx={{
      height: 65,
      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
    }}
    >
    {menuItems.slice(0, 4).map((item) => (
      <BottomNavigationAction
      key={item.path}
      label={t(item.text)}
      value={item.path}
      icon={item.icon}
      />
    ))}
    <BottomNavigationAction
    label={t('sidebar.language')}
    icon={<LanguageIcon />}
    onClick={handleLanguageClick}
    />
    <BottomNavigationAction
    label={t('sidebar.public')}
    icon={<PublicIcon />}
    value="/public"
    />
    </BottomNavigation>
    </Box>
    
    <Dialog
    open={logoutDialogOpen}
    onClose={handleLogoutCancel}
    aria-labelledby="logout-dialog-title"
    >
    <DialogTitle id="logout-dialog-title">
    {t('auth.confirmLogout')}
    </DialogTitle>
    <DialogContent>
    <Typography>
    {t('auth.areYouSureYouWantToLogout')}
    </Typography>
    </DialogContent>
    <DialogActions>
    <Button onClick={handleLogoutCancel} color="inherit">
    {t('cancel')}
    </Button>
    <Button onClick={handleLogoutConfirm} color="error" variant="contained" autoFocus>
    {t('logout')}
    </Button>
    </DialogActions>
    </Dialog>
    
    <LanguageDialog
    open={languageDialogOpen}
    onClose={() => setLanguageDialogOpen(false)}
    />
    </>
  );
};

export default Sidebar;
